/**
 * shareCard.ts
 *
 * Renders the Choose artifact card to a Canvas and triggers a PNG download.
 * No external dependencies — pure Canvas API.
 *
 * Card dimensions: 1080×1080 (Instagram-native square)
 */

const W = 1080
const H = 1080
const PAD = 96

/** Wrap text to fit within maxWidth, returns array of lines */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ')
  const lines: string[] = []
  let current = ''
  for (const word of words) {
    const test = current ? `${current} ${word}` : word
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current)
      current = word
    } else {
      current = test
    }
  }
  if (current) lines.push(current)
  return lines
}

export async function saveChoiceCard(params: {
  choiceText: string
  choiceStem: string
  emotion: string
  date: string
}): Promise<void> {
  const { choiceText, choiceStem, emotion, date } = params

  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')!

  // ── Background gradient ────────────────────────────────────────
  const grad = ctx.createLinearGradient(0, 0, W, H)
  grad.addColorStop(0, '#3D1F62')
  grad.addColorStop(1, '#6B3FA0')
  ctx.fillStyle = grad
  ctx.beginPath()
  roundRect(ctx, 0, 0, W, H, 64)
  ctx.fill()

  // Subtle noise texture overlay (very faint radial, gives depth)
  const noiseGrad = ctx.createRadialGradient(W * 0.3, H * 0.25, 0, W * 0.5, H * 0.5, W * 0.75)
  noiseGrad.addColorStop(0, 'rgba(255,255,255,0.04)')
  noiseGrad.addColorStop(1, 'rgba(0,0,0,0.0)')
  ctx.fillStyle = noiseGrad
  ctx.fillRect(0, 0, W, H)

  // ── Choice stem label ──────────────────────────────────────────
  const stemLabel = choiceStem.replace('...', '').toUpperCase()
  ctx.font = '500 28px Inter, system-ui, sans-serif'
  ctx.fillStyle = 'rgba(255,255,255,0.38)'
  ctx.letterSpacing = '4px'
  ctx.textAlign = 'center'
  ctx.fillText(stemLabel, W / 2, PAD + 48)

  // ── Choice text (large italic) ─────────────────────────────────
  ctx.font = 'italic 400 64px Georgia, "Playfair Display", serif'
  ctx.fillStyle = 'white'
  ctx.letterSpacing = '-0.5px'
  ctx.textAlign = 'center'

  const maxTextWidth = W - PAD * 2 - 80
  const lines = wrapText(ctx, choiceText, maxTextWidth)
  const lineHeight = 88
  const blockHeight = lines.length * lineHeight
  const textStartY = (H - blockHeight) / 2 - 40

  lines.forEach((line, i) => {
    ctx.fillText(line, W / 2, textStartY + i * lineHeight)
  })

  // ── Divider line ───────────────────────────────────────────────
  const dividerY = textStartY + blockHeight + 64
  ctx.strokeStyle = 'rgba(255,255,255,0.18)'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(W / 2 - 48, dividerY)
  ctx.lineTo(W / 2 + 48, dividerY)
  ctx.stroke()

  // ── Date + emotion ─────────────────────────────────────────────
  ctx.font = '400 26px Inter, system-ui, sans-serif'
  ctx.fillStyle = 'rgba(255,255,255,0.32)'
  ctx.letterSpacing = '0px'
  ctx.textAlign = 'center'
  ctx.fillText(`${date} · ${emotion}`, W / 2, dividerY + 44)

  // ── Branding (bottom) ──────────────────────────────────────────
  ctx.font = '500 22px Inter, system-ui, sans-serif'
  ctx.fillStyle = 'rgba(255,255,255,0.18)'
  ctx.letterSpacing = '2px'
  ctx.textAlign = 'center'
  ctx.fillText('THE INNER AWAKENING', W / 2, H - PAD)

  // ── Download ───────────────────────────────────────────────────
  const blob = await new Promise<Blob>((resolve) => {
    canvas.toBlob(b => resolve(b!), 'image/png')
  })

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `inner-awakening-${date.replace(/,?\s/g, '-').toLowerCase()}.png`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/** Canvas rounded rect helper (native roundRect not available in all browsers) */
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

// ─── Insight Card (shareable) ──────────────────────────────────────────────
// 9:16 vertical (1080×1920) — the primary share format for TikTok / Reels /
// Stories. This is the "save this card" viral artifact.
const IW = 1080
const IH = 1920
const IPAD = 120

/**
 * Deliver a rendered canvas as a PNG: prefer the native share sheet (one tap
 * to send to a friend / post to Stories) and fall back to a download when the
 * platform can't share files (most desktops). If the user cancels the share
 * sheet, we do NOT also download.
 */
export async function deliverPng(canvas: HTMLCanvasElement, filename: string, shareText: string): Promise<void> {
  const blob = await new Promise<Blob>((resolve) => canvas.toBlob(b => resolve(b!), 'image/png'))
  const file = new File([blob], filename, { type: 'image/png' })

  // navigator.canShare / share with files isn't in all TS DOM libs — guard via any.
  const nav = navigator as Navigator & {
    canShare?: (data?: { files?: File[] }) => boolean
    share?: (data: { files?: File[]; title?: string; text?: string }) => Promise<void>
  }
  if (nav.canShare?.({ files: [file] }) && nav.share) {
    try {
      await nav.share({ files: [file], title: 'The Inner Awakening', text: shareText })
      return
    } catch (err) {
      // User dismissed the share sheet — respect that, don't force a download.
      if ((err as Error).name === 'AbortError') return
      // Any other error (e.g. share not actually supported) → fall through to download.
    }
  }

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Render an Insight Card to a 9:16 PNG and offer it via share/download.
 *
 * Copy rule (clinical, per Sage): the card surfaces ONLY the cleared content —
 * the card label, the named pattern (title), the reflection message, and the
 * arrival emotion/intensity. It never includes the user's raw story text or any
 * event-reframe. The domain footer is baked in so re-shared screenshots keep
 * attribution.
 */
export function buildInsightCardCanvas(params: {
  cardLabel: string
  cardTitle: string
  cardMessage: string
  gradientFrom: string
  gradientTo: string
  emotion: string
  intensity: number
  date: string
}): HTMLCanvasElement {
  const { cardLabel, cardTitle, cardMessage, gradientFrom, gradientTo, emotion, intensity, date } = params

  const canvas = document.createElement('canvas')
  canvas.width = IW
  canvas.height = IH
  const ctx = canvas.getContext('2d')!

  // ── Background gradient (matches the on-screen card) ───────────────
  const grad = ctx.createLinearGradient(0, 0, IW, IH)
  grad.addColorStop(0, gradientFrom)
  grad.addColorStop(1, gradientTo)
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, IW, IH)

  // Soft radial glow for depth
  const glow = ctx.createRadialGradient(IW * 0.5, IH * 0.34, 0, IW * 0.5, IH * 0.42, IW * 0.95)
  glow.addColorStop(0, 'rgba(255,255,255,0.06)')
  glow.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = glow
  ctx.fillRect(0, 0, IW, IH)

  ctx.textAlign = 'center'
  const maxW = IW - IPAD * 2

  // ── Measure the content block so we can center it vertically ───────
  ctx.font = 'italic 400 72px Georgia, "Playfair Display", serif'
  const titleLines = wrapText(ctx, cardTitle, maxW)
  const titleLH = 92

  ctx.font = '400 34px Inter, system-ui, sans-serif'
  const msgLines = wrapText(ctx, cardMessage, maxW)
  const msgLH = 54

  const LABEL_TO_TITLE = 96
  const TITLE_TO_DIV = 56
  const DIV_TO_MSG = 60
  const MSG_TO_META = 64
  const blockH =
    LABEL_TO_TITLE + titleLines.length * titleLH + TITLE_TO_DIV + DIV_TO_MSG +
    msgLines.length * msgLH + MSG_TO_META

  let y = Math.max(IPAD + 80, (IH - blockH) / 2 - 40)

  // ── Label ──────────────────────────────────────────────────────────
  ctx.font = '500 28px Inter, system-ui, sans-serif'
  ctx.fillStyle = 'rgba(255,255,255,0.5)'
  ctx.letterSpacing = '4px'
  ctx.fillText(cardLabel.toUpperCase(), IW / 2, y)
  ctx.letterSpacing = '0px'
  y += LABEL_TO_TITLE

  // ── Title (named pattern) ──────────────────────────────────────────
  ctx.font = 'italic 400 72px Georgia, "Playfair Display", serif'
  ctx.fillStyle = 'white'
  titleLines.forEach((line, i) => ctx.fillText(line, IW / 2, y + i * titleLH))
  y += titleLines.length * titleLH + TITLE_TO_DIV

  // ── Divider ────────────────────────────────────────────────────────
  ctx.strokeStyle = 'rgba(255,255,255,0.2)'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(IW / 2 - 40, y)
  ctx.lineTo(IW / 2 + 40, y)
  ctx.stroke()
  y += DIV_TO_MSG

  // ── Message ────────────────────────────────────────────────────────
  ctx.font = '400 34px Inter, system-ui, sans-serif'
  ctx.fillStyle = 'rgba(255,255,255,0.82)'
  msgLines.forEach((line, i) => ctx.fillText(line, IW / 2, y + i * msgLH))
  y += msgLines.length * msgLH + MSG_TO_META

  // ── Emotion · intensity · date ─────────────────────────────────────
  ctx.font = '400 28px Inter, system-ui, sans-serif'
  ctx.fillStyle = 'rgba(255,255,255,0.42)'
  ctx.fillText(`${emotion} · arrived at ${intensity} · ${date}`, IW / 2, y)

  // ── Branding + domain footer (baked in for attribution) ────────────
  ctx.font = '500 24px Inter, system-ui, sans-serif'
  ctx.fillStyle = 'rgba(255,255,255,0.22)'
  ctx.letterSpacing = '3px'
  ctx.fillText('THE INNER AWAKENING', IW / 2, IH - IPAD - 34)
  ctx.letterSpacing = '1px'
  ctx.font = '400 24px Inter, system-ui, sans-serif'
  ctx.fillStyle = 'rgba(255,255,255,0.16)'
  ctx.fillText('the-inner-awakening.com', IW / 2, IH - IPAD + 6)
  ctx.letterSpacing = '0px'

  return canvas
}

/** Render an Insight Card and immediately deliver it (share sheet / download). */
export async function saveInsightCard(params: Parameters<typeof buildInsightCardCanvas>[0]): Promise<void> {
  const canvas = buildInsightCardCanvas(params)
  const filename = `insight-card-${params.date.replace(/,?\s/g, '-').toLowerCase()}.png`
  await deliverPng(canvas, filename, `A reflection from my Inner Awakening session — "${params.cardTitle}"`)
}
